'use client'
import { NavbarProps, NavItem, navItems } from '@/constants/navs'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { memo, Suspense, useEffect, useLayoutEffect, useState } from 'react'
import { Button } from './button'
import { Logo } from './logo'
import { handleUETClick } from '@/lib/utils'
import { landingpage_aicreate, landingpage_login } from '@/constants/trace'

function buildLoginPath(queryString: string, extraQuery?: string) {
  if (!queryString && !extraQuery) return 'https://app.aitubiao.com/sign-in'
  if (!queryString) return `https://app.aitubiao.com/sign-in?${extraQuery}`
  if (!extraQuery) return `https://app.aitubiao.com/sign-in?${queryString}`
  return `https://app.aitubiao.com/sign-in?${queryString}&${extraQuery}`
}

function isNavItemActive(pathname: string, link?: string) {
  if (!link) return false
  if (link === '/') return pathname === '/'
  return pathname === link || pathname.startsWith(`${link}/`)
}

export const Navbar = () => {
  const pathname = usePathname()
  const { scrollY } = useScroll()
  const [visible, setVisible] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useLayoutEffect(() => {
    if (!mounted) {
      return
    }

    // 路由切换时同步按当前滚动位置收口 visible，避免沿用上一页状态先渲染一帧，造成导航条“弹一下”的观感。
    setVisible(window.scrollY > 100)
  }, [mounted, pathname])

  useMotionValueEvent(scrollY, 'change', latest => {
    if (!mounted) {
      return
    }

    setVisible(latest > 100)
  })

  return (
    <Suspense>
      <motion.div className="fixed inset-x-0 top-0 z-50 w-full">
        <DesktopNav visible={visible} navItems={navItems} />
        <MobileNav visible={visible} navItems={navItems} />
      </motion.div>
    </Suspense>
  )
}

const DesktopNav = memo(({ navItems, visible }: NavbarProps) => {
  const [hovered, setHovered] = useState<number | null>(null)
  const [pendingIndex, setPendingIndex] = useState<number | null>(null)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const queryString = searchParams.toString()
  const loginPath = buildLoginPath(queryString)
  const activeIndex = navItems.findIndex(navItem => isNavItemActive(pathname, navItem.link))
  const highlightedIndex = pendingIndex ?? hovered ?? activeIndex

  useEffect(() => {
    setHovered(null)
    setPendingIndex(null)
  }, [pathname])

  return (
    <motion.div
      onMouseLeave={() => {
        if (pendingIndex !== null) {
          return
        }

        setHovered(null)
      }}
      animate={{
        backdropFilter: visible ? 'blur(6px)' : 'none',
        boxShadow: visible
          ? '0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset'
          : 'none',
        maxWidth: visible ? '900px' : '1280px',
        y: visible ? 20 : 0
      }}
      transition={{
        type: 'tween',
        duration: 0.22,
        ease: 'easeOut'
      }}
      className={cn(
        'relative z-[40] mx-auto hidden w-full max-w-7xl flex-row items-center justify-between self-start rounded-full px-4 py-2 lg:flex',
        visible ? 'bg-white/60 backdrop-blur-sm' : 'bg-white',
        'transform-gpu'
      )}
    >
      <div className="flex w-[240px] items-center justify-start">
        <Logo white={false} />
      </div>
      <motion.div className="absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-base font-medium transition duration-200 hover:text-zinc-800 lg:flex lg:space-x-2">
        {navItems.map((navItem: NavItem, idx: number) => {
          const isHighlighted = highlightedIndex === idx
          // 这里不用 cn 重新拼接类名，直接和 SSR 产出的顺序保持一致，避免 hydration 时只因 class 字符串顺序不同就报 mismatch。
          const navTextClassName = isHighlighted
            ? 'relative z-20 transition duration-200 text-inherit'
            : 'relative z-20 text-[#353745] transition duration-200'

          return (
            <Link
              onMouseEnter={() => {
                if (pendingIndex !== null) {
                  return
                }

                setHovered(idx)
              }}
              className="relative px-4 py-2"
              key={`link=${idx}`}
              onClick={event => {
                event.preventDefault()
                if (navItem.link) {
                  setHovered(idx)
                  setPendingIndex(idx)
                  // 修改当前 link 携带路径参数。
                  window.location.href = queryString ? `${navItem.link}?${queryString}` : navItem.link
                }
              }}
              href={navItem.link || '/#'}
            >
              {isHighlighted && <motion.div layoutId="hovered" className="absolute inset-0 h-full w-full rounded-full bg-[#5a42eb]/10" />}
              <span className={navTextClassName}>{navItem.name}</span>
            </Link>
          )
        })}
      </motion.div>
      <div className="flex w-[240px] items-center justify-end gap-4" onClickCapture={handleUETClick}>
        <Button as={Link} href={loginPath} traceEvent={{ event: landingpage_login }} variant="secondary" className="hidden md:flex">
          登录
        </Button>
        <Button
          as={Link}
          href={loginPath}
          traceEvent={{ event: landingpage_aicreate }}
          variant="primary"
          className="hidden justify-between fill-current hover:text-white md:flex"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="currentColor">
            <path d="M3.79644 3.27116C3.72143 3.00434 3.34319 3.00434 3.26817 3.27116L3.05789 4.01918C2.75368 5.10127 1.88379 5.93148 0.788681 6.18487C0.605892 6.22717 0.585588 6.47937 0.759256 6.55036L0.866576 6.59423C1.93545 7.03117 2.73611 7.94599 3.02757 9.06333L3.21859 9.79561C3.29283 10.0802 3.70203 10.0648 3.75465 9.77541L3.82506 9.38815C4.05709 8.11198 4.94068 7.05061 6.15365 6.59107L6.27996 6.54321C6.45576 6.47661 6.43861 6.22251 6.25546 6.18014C5.17298 5.92966 4.31312 5.10902 4.01242 4.03941L3.79644 3.27116ZM9.49539 5.25904C9.74398 4.36355 11.0064 4.3434 11.2835 5.2305L11.4508 5.76616C12.0578 7.70988 13.4795 9.29389 15.3465 10.1067C16.0539 10.4147 16.0412 11.4229 15.3299 11.7217C13.3441 12.5559 11.8361 14.2535 11.2505 16.3263L10.794 17.9424C10.6592 18.4197 9.98656 18.4306 9.83635 17.9579L9.27338 16.1864C8.64024 14.194 7.1559 12.5758 5.2285 11.7661C4.49788 11.4592 4.50195 10.418 5.23837 10.1253C7.29627 9.30727 8.85289 7.57351 9.44524 5.4397L9.49539 5.25904ZM14.6842 14.2591C14.7453 14.0505 15.0407 14.0505 15.1017 14.2591L15.1584 14.4528C15.2941 14.9159 15.6616 15.2748 16.1279 15.3993C16.2804 15.44 16.3028 15.6471 16.1625 15.7195L16.0329 15.7864C15.612 16.0037 15.2916 16.3755 15.1389 16.824L15.0444 17.1015C14.9958 17.2443 14.7935 17.2432 14.7465 17.0999L14.6366 16.7651C14.4914 16.3229 14.1707 15.9599 13.7497 15.7614L13.6446 15.7118C13.5019 15.6445 13.5207 15.4359 13.6731 15.3952C14.1301 15.2732 14.4903 14.9216 14.6232 14.4676L14.6842 14.2591Z" />
          </svg>
          <span className="ml-1">AI创作</span>
        </Button>
      </div>
    </motion.div>
  )
})

const MobileNav = memo(({ navItems, visible }: NavbarProps) => {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const queryString = searchParams.toString()
  const loginPath = buildLoginPath(queryString)

  return (
    <>
      <motion.div
        animate={{
          backdropFilter: visible ? 'blur(6px)' : 'none',
          boxShadow: visible
            ? '0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset'
            : 'none',
          width: visible ? '90%' : '100%',
          y: visible ? 20 : 0,
          borderRadius: open ? '4px' : '2rem',
          paddingRight: visible ? '12px' : '0px',
          paddingLeft: visible ? '12px' : '0px'
        }}
        transition={{
          type: 'tween',
          duration: 0.22,
          ease: 'easeOut'
        }}
        className={cn(
          'relative z-50 mx-auto flex w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between px-0 py-2 lg:hidden',
          visible ? 'bg-white/60 backdrop-blur-sm' : 'bg-white',
          'transform-gpu'
        )}
      >
        <div className="flex w-full flex-row items-center justify-between">
          <Logo />
          {open ? <X className="text-black" onClick={() => setOpen(!open)} /> : <Menu className="text-black" onClick={() => setOpen(!open)} />}
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{
                opacity: 0
              }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-white px-4 py-8 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]"
            >
              {navItems.map((navItem: NavItem, idx: number) => {
                const isActive = isNavItemActive(pathname, navItem.link)

                return (
                  <Link
                    key={`link=${idx}`}
                    href={navItem.link || '/#'}
                    onClick={event => {
                      event.preventDefault()
                      if (navItem.link) {
                        // 修改当前 link 携带路径参数。
                        window.location.href = queryString ? `${navItem.link}?${queryString}` : navItem.link
                        setOpen(false)
                      }
                    }}
                    className={cn('relative', isActive ? 'font-medium text-[#685EF6]' : 'text-neutral-600')}
                  >
                    <motion.span className="block">{navItem.name} </motion.span>
                  </Link>
                )
              })}
              <div
                className="w-full"
                onClickCapture={() => {
                  handleUETClick()
                  setOpen(false)
                }}
              >
                <Button as={Link} href={loginPath} traceEvent={{ event: landingpage_login }} variant="primary" className="block w-full">
                  登录
                </Button>
              </div>
              <div
                className="w-full"
                onClickCapture={() => {
                  handleUETClick()
                  setOpen(false)
                }}
              >
                <Button
                  as={Link}
                  href={loginPath}
                  traceEvent={{ event: landingpage_aicreate }}
                  variant="primary"
                  className="flex w-full items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="currentColor">
                    <path d="M3.79644 3.27116C3.72143 3.00434 3.34319 3.00434 3.26817 3.27116L3.05789 4.01918C2.75368 5.10127 1.88379 5.93148 0.788681 6.18487C0.605892 6.22717 0.585588 6.47937 0.759256 6.55036L0.866576 6.59423C1.93545 7.03117 2.73611 7.94599 3.02757 9.06333L3.21859 9.79561C3.29283 10.0802 3.70203 10.0648 3.75465 9.77541L3.82506 9.38815C4.05709 8.11198 4.94068 7.05061 6.15365 6.59107L6.27996 6.54321C6.45576 6.47661 6.43861 6.22251 6.25546 6.18014C5.17298 5.92966 4.31312 5.10902 4.01242 4.03941L3.79644 3.27116ZM9.49539 5.25904C9.74398 4.36355 11.0064 4.3434 11.2835 5.2305L11.4508 5.76616C12.0578 7.70988 13.4795 9.29389 15.3465 10.1067C16.0539 10.4147 16.0412 11.4229 15.3299 11.7217C13.3441 12.5559 11.8361 14.2535 11.2505 16.3263L10.794 17.9424C10.6592 18.4197 9.98656 18.4306 9.83635 17.9579L9.27338 16.1864C8.64024 14.194 7.1559 12.5758 5.2285 11.7661C4.49788 11.4592 4.50195 10.418 5.23837 10.1253C7.29627 9.30727 8.85289 7.57351 9.44524 5.4397L9.49539 5.25904ZM14.6842 14.2591C14.7453 14.0505 15.0407 14.0505 15.1017 14.2591L15.1584 14.4528C15.2941 14.9159 15.6616 15.2748 16.1279 15.3993C16.2804 15.44 16.3028 15.6471 16.1625 15.7195L16.0329 15.7864C15.612 16.0037 15.2916 16.3755 15.1389 16.824L15.0444 17.1015C14.9958 17.2443 14.7935 17.2432 14.7465 17.0999L14.6366 16.7651C14.4914 16.3229 14.1707 15.9599 13.7497 15.7614L13.6446 15.7118C13.5019 15.6445 13.5207 15.4359 13.6731 15.3952C14.1301 15.2732 14.4903 14.9216 14.6232 14.4676L14.6842 14.2591Z" />
                  </svg>
                  从AI新建
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  )
})

DesktopNav.displayName = 'DesktopNav'
MobileNav.displayName = 'MobileNav'
